import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AppToolbar, CURRENT_YEAR_MAX_DATE, DatePickerComponent, DatePickerWrapper, MIN_DATE } from "../components/AppToolbar";
import { SimpleAutoComplete } from "../components/SimpleAutoComplete";
import AutoCompleteUIDTO from "../dto/AutoCompleteUIDTO";
import TableBasedCrud from "../external/ensolvers-core-frontend-mui/components/TableBaseCrud";
import useLocalStorage, { STORAGE_KEYS } from "../hooks/useLocalStorage";
import WorkLogSummary from "../models/WorkLogSummary";
import ProjectService from "../services/ProjectService";
import WorkLogService from "../services/WorkLogService";
import { Link, useLocation } from "react-router-dom";
import { Routes } from "../types/Routes";
import { ColorUtils } from "../utils/ColorUtils";
import { AppContainerFullHeight } from "../components/AppContainerFullHeight";
import { useStoredDate } from "../hooks/useStoredDate";
import { EMPTY_AUTOCOMPLETE } from "../dto/AutoCompleteUIDTO";
import { trace } from "../external/ensolvers-core-frontend-mui/submodules/fox-typescript/utils/Logging";
import OpsDateUtils from "../utils/OpsDateUtils";
import { SpinnerWrapper } from "../components/SpinnerWrapper";
import { makeStyles } from "@material-ui/core";
import FilterDialog, { FilterDialogButton } from "../components/FilterDialog";
import useWindowSize from "../hooks/useWindowSize";
import { SwitchCustom } from "../utils/SwitchCustom";
import useTableHeight from "../hooks/useTableHeight";
import useUpdateUrl from "../hooks/useUpdateUrl";
import { Box } from "@mui/material";

export default function WorklogSummaryList() {
  const { t } = useTranslation();
  const classes = useStyles();

  const [projectsNames, setProjectNames] = useState<AutoCompleteUIDTO[]>([]);
  const [selectedProject, setSelectedProject] = useState<AutoCompleteUIDTO>();
  const [selectedDate, setSelectedDate] = useStoredDate();
  const [assigned, setAssigned] = useLocalStorage<boolean>(STORAGE_KEYS.STORED_ASSIGNED_TO_ME, false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState(false);
  const { isMobileWidth } = useWindowSize();
  const { bottomSectionRef, calculateMaxHeight } = useTableHeight();

  const searchParams = new URLSearchParams(useLocation().search);
  const project = searchParams.get("project");
  const month = searchParams.get("month");
  const year = searchParams.get("year");
  const assignedToMe = searchParams.get("assigned");
  const {updateUrl} = useUpdateUrl(Routes.WORKLOGS_SUMMARY, searchParams);
  const [storedProject, setStoredProject] = useLocalStorage<AutoCompleteUIDTO>(STORAGE_KEYS.STORED_PROJECT, EMPTY_AUTOCOMPLETE);

  useEffect(() => {
    getAllProjectsNames();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assigned]);

  useEffect(() => {
    if (project !== null && projectsNames) {
      const projectFilter = projectsNames.find((p) => p.externalId === project);
      if (projectFilter) setSelectedProject(projectFilter);
    } else if (storedProject !== null && projectsNames) {
      const projectFilter = projectsNames.find((p) => p.externalId === storedProject.externalId);
      if (projectFilter) setSelectedProject(projectFilter);
      searchParams.set("project", storedProject.externalId);
    }

    const date = new Date();

    if (month !== null) {
      date.setMonth(parseInt(month));
      setSelectedDate(date);
    } else {
      searchParams.set("month", (date.getMonth() - 1).toString());
    }

    if (year !== null) {
      date.setFullYear(parseInt(year));
      setSelectedDate(date);
    } else {
      searchParams.set("year", date.getFullYear().toString());
    }

    if (assignedToMe !== null) {
      setAssigned(assignedToMe === "true");
    } else if (assigned !== undefined) {
      searchParams.set("assigned", assigned.toString());
    }

    updateUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectsNames]);

  const handleChangeAssignedWorklog = () => {
    setAssigned(!assigned);
    setSelectedProject(undefined);
    searchParams.delete("project");
    searchParams.set("assigned", (!assigned).toString());
    setStoredProject(EMPTY_AUTOCOMPLETE);
    updateUrl();
  };

  const getAllProjectsNames = () => {
    setIsLoading(true);
    ProjectService.findAllProjectsNames(false, assigned)
      .then((response) => {
        setProjectNames(response.getContent());
      })
      .catch((error) => trace("Error getting projects", error))
      .finally(() => setIsLoading(false));
  };

  const renderProjectNameAutoComplete = (
    <SimpleAutoComplete
      label="Project name"
      items={projectsNames}
      value={selectedProject ? selectedProject : EMPTY_AUTOCOMPLETE}
      onChange={(newValue) => {
        setSelectedProject(newValue);
        setStoredProject(newValue);
        searchParams.set("project", newValue.externalId);
        updateUrl();
      }}
    />
  );

  const renderDatePickerAndAssignToMe = (
    <>
      <DatePickerWrapper>
        <DatePickerComponent
          views="month"
          label="Month"
          value={selectedDate}
          inputFormat="MMMM"
          onChange={(selectedDate) => {
            setSelectedDate(selectedDate!);
            searchParams.set("month", selectedDate!.getMonth().toString());
            updateUrl();
          }}
        />
        <DatePickerComponent
          views="year"
          label="Year"
          value={selectedDate}
          inputFormat="yyyy"
          minDate={MIN_DATE}
          maxDate={CURRENT_YEAR_MAX_DATE}
          onChange={(selectedDate) => {
            setSelectedDate(selectedDate!);
            searchParams.set("year", selectedDate!.getFullYear().toString());
            updateUrl();
          }}
        />
      </DatePickerWrapper>
      <SwitchCustom
        checked={assigned}
        onChange={handleChangeAssignedWorklog}
        color={ColorUtils.STRONG_ENSOLVERS_ORANGE}
        label={"Assigned to me"}
        size={"medium"}
      />
    </>
  );
  const renderFilters = () => {
    const mobileFilters = (
      <div className={classes.mobileFilters}>
        {renderProjectNameAutoComplete}
        <FilterDialogButton isOpen={isOpen} setIsOpen={setIsOpen} />
      </div>
    );

    const desktopFilters = (
      <div className={classes.desktopFilters}>
        {renderProjectNameAutoComplete}
        {renderDatePickerAndAssignToMe}
      </div>
    );
    return isMobileWidth() ? mobileFilters : desktopFilters;
  };

  const renderDialogFilters = <div className={classes.dialogFilters}>{renderDatePickerAndAssignToMe}</div>;

  return (
    <>
      <SpinnerWrapper isLoading={isLoading}>
        <AppContainerFullHeight
          bottomSectionRef={bottomSectionRef}
          topSection={
            <>
              <AppToolbar title={t("WorkLogs Summary")}>{renderFilters()}</AppToolbar>
            </>
          }
          bottomSection={
            <Box sx={{boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)", borderRadius: "10px"}}>
              <SpinnerWrapper isLoading={isLoading}>
                <TableBasedCrud<WorkLogSummary>
                  maxHeight={calculateMaxHeight(false)}
                  stickyHeader
                  stickyPagination
                  elementName={t("Assignation")}
                  onDataFetch={(pageNumber: number, count: number) =>
                    WorkLogService.findWorklogsSummary(
                      pageNumber,
                      count,
                      selectedDate.getFullYear(),
                      selectedDate.getMonth() + 1,
                      selectedProject?.externalId ? selectedProject.externalId : ""
                    ).then((result) => result.getContent())
                  }
                  hideActions={true}
                  denseTable
                  renderDialogContent={() => <></>}
                  tableColumns={[
                    {
                      header: t("Assignation"),
                      content: (instance) => (
                        <>
                          <Link to={`${Routes.WORKLOG}/${instance.userExternalId}`}>{instance.userFullName}</Link>
                        </>
                      ),
                    },
                    {
                      header: t("Role"),
                      content: (instance) => (
                        <>
                          <Link to={`${Routes.POSITIONS}?project=${selectedProject?.externalId}`}>{instance.positionName}</Link>
                        </>
                      ),
                    },
                    {
                      header: t("Start Date"),
                      content: (instance) => <>{OpsDateUtils.formatInUTCAsDDMMMYYY(instance.startDate)}</>,
                    },
                    {
                      header: t("End Date"),
                      content: (instance) => <>{instance.endDate ? OpsDateUtils.formatInUTCAsDDMMMYYY(instance.endDate) : ""}</>,
                    },
                    {
                      header: t("Missing days"),
                      content: (instance) => (
                        <>{instance.missingDays < 0 ? `Extra days + ${-instance.missingDays}` : instance.missingDays}</>
                      ),
                    },
                  ]}
                  stylesForRowIfCondition={(instance) => {
                    if (instance.missingDays > 1) {
                      return { backgroundColor: ColorUtils.LIGHT_RED };
                    } else if (instance.missingDays === 1) {
                      return { backgroundColor: ColorUtils.LIGHT_YELLOW };
                    }
                    return undefined;
                  }}
                />
              </SpinnerWrapper>
            </Box>
          }
        />
      </SpinnerWrapper>

      {/* Dialog to manage filters  */}
      <FilterDialog title={"Worklogs"} useCustomStyle={true} setIsOpen={setIsOpen} isOpen={isOpen}>
        {renderDialogFilters}
      </FilterDialog>
    </>
  );
}

const useStyles = makeStyles((theme) => ({
  desktopFilters: {
    borderRadius: "4px",
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '1rem',
    border: `1px solid ${ColorUtils.LIGHTER_GRAY}`,
    boxSizing: 'inherit',
    boxShadow: "10px 10px 20px rgb(0, 0, 0, 0.05)",
    display: "flex",
    flexDirection: "row",
    "&> div:first-child": {
      minWidth: "150px",
      marginLeft: "0",
    },
    "&> div:nth-child(2)": {
      "&> div": {
        width: "100% !important",
        maxWidth: "200px !important",
        minWidth: "0 !important",
      },
    },
  },
  mobileFilters: {
    "&> div:first-child": {
      minWidth: "150px",
      width: "100%",
      marginLeft: "0",
    },
    width: "100%",
    display: "flex",
    flexDirection: "column",
    rowGap: "10px",
    padding: "5px",
  },
  dialogFilters: {
    padding: "30px 15px",
    rowGap: "10px",
    display: "flex",
    flexDirection: "column",
    "& div": {
      margin: "0 !important",
      width: "100% !important",
    },
  },
  [theme.breakpoints.down("sm")]: {
    desktopFilters: {
      width: "100% !important",
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gridRowGap: "10px",
      gridColumnGap: "10px",
      "&> div": {
        width: "100%",
      },
      "& button": {
        width: "fit-content",
        "grid-column": "1/span 2",
      },
      "&> div:nth-child(2)": {
        "&> div": {
          width: "100% !important",
          maxWidth: "none !important",
        },
      },
    },
  },
}),
{ index: 1 }
);
